import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, cn, Iskelet, Kart, Rozet } from "@koclukpanel/ui";
import {
  ApiHatasi,
  logGorunumleri,
  uyariKurallari,
  uyariKuraliGuncelle,
  type UyariKurali,
} from "./api";

/**
 * UYARI KURALLARI VE KAYITLI GORUNUMLER — F-LOG-20/21/32
 *
 * ================================================================
 * "AKTIF" YETMEZ, "DEGERLENDIRILIYOR" DA GEREKIYOR
 * ================================================================
 * Bir kural aktif isaretli olabilir ve yine de HIC calismiyor
 * olabilir — filtresinde henuz uygulanmamis bir alan varsa. Bunu
 * ayirt etmeyen bir ekran, tam da 03.08.2026'da bulunan hatayi
 * gizlerdi: on kural "aktif" gorunuyordu, hicbiri calismiyordu.
 *
 * ================================================================
 * EKRAN 07.09.2026'YA KADAR SALT OKUNURDU
 * ================================================================
 * F-LOG-33 acikca *"uyari kurallari eklenebilir, KAPATILABILIR,
 * esikleri DEGISTIRILEBILIR"* diyor. Sunucuda yazma ucu HIC YOKTU ve
 * ekranda da duzenleme yoktu — yani `isLocked` sutunu bir seyi
 * koruyordu ama korudugu sey zaten mumkun degildi.
 *
 * Ayni sinifin tersi: kural motoru dogru calisiyordu, ona giden
 * YONETIM yolu yoktu.
 */

function tarih(iso: string | null): string {
  if (iso === null) return "hiç tetiklenmedi";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function UyariKurallari() {
  const kurallar = useQuery({ queryKey: ["uyari-kurallari"], queryFn: uyariKurallari });
  const gorunumler = useQuery({ queryKey: ["log-gorunumleri"], queryFn: logGorunumleri });

  if (kurallar.isPending) return <Iskelet satir={4} />;
  if (kurallar.isError) {
    /*
      ================================================================
      HER HATA "YETKİNİZ YOK" DEĞİL
      ================================================================
      Tek cümle bütün hataları karşılıyordu. Bu ekranda o yanlış,
      başka ekranlardakinden daha pahalı: burada söylenen şey
      "alarmların durumunu göremezsiniz". Yetkisi olduğunu bilen bir
      teknik yönetici bunu okuyup **yetki sorunu sanır** ve
      alarmların çalışıp çalışmadığını sormayı bırakır — oysa asıl
      olasılık sunucunun cevap verememesi ve tam o anda alarm
      durumunun bilinmiyor olması.
    */
    const yetkisiz = kurallar.error instanceof ApiHatasi && kurallar.error.durum === 403;
    return (
      <p className="text-notr-600 text-sm">
        {yetkisiz
          ? "Uyarı kuralları yalnızca sistem kategorisi yetkisi olanlara gösterilir."
          : "Uyarı kuralları alınamadı — alarmların şu an çalışıp çalışmadığı bilinmiyor. Yeniden deneyin."}
      </p>
    );
  }

  const calismayanlar = kurallar.data.filter((k) => !k.degerlendiriliyor);

  return (
    <div className="flex flex-col gap-5">
      {calismayanlar.length > 0 && (
        /*
          EN USTTE ve uyari renginde: bu kurallar panelde aktif
          gorunuyor ama calismiyor. Listenin icinde kaybolsaydi
          "alarm var" sanilmaya devam edilirdi.
        */
        <div className="rounded-kart shadow-kart border-uyari-500/30 bg-uyari-500/10 border p-3">
          <p className="text-notr-800 text-sm">
            <strong>{calismayanlar.length} kural şu an değerlendirilmiyor.</strong> Filtrelerinde
            henüz uygulanmayan bir alan var; “aktif” görünseler de tetiklenmiyorlar.
          </p>
          <p className="text-notr-600 mt-1 text-xs">{calismayanlar.map((k) => k.ad).join(" · ")}</p>
        </div>
      )}

      <section className="flex flex-col gap-2">
        <h3 className="text-notr-800 text-sm font-semibold">
          Uyarı kuralları ({kurallar.data.length})
        </h3>
        {/*
          KİLİDİN SEBEBİ YAZILIYOR — VE YALNIZCA KİLİTLİ KURAL VARKEN.

          Rozet "kapatılamaz" diyordu ama **neden** olduğunu
          söylemiyordu. Sebepsiz bir yasak, keyfî bir kısıt gibi
          okunur ve keyfî görünen kısıtlar etrafından dolaşılmaya
          çalışılır.

          Cümle tek ve genel: kural adına bakıp her kurala ayrı gerekçe
          yazmak, ada göre dallanmak olurdu — kural yeniden
          adlandırıldığı gün gerekçe sessizce yanlış kurala yapışırdı.
          Genel cümle iki kilitli kuralın ikisi için de doğru.
        */}
        {kurallar.data.some((r) => r.kilitli) && (
          <p className="text-notr-600 text-xs">
            Kilitli kurallar kapatılamaz: kapatılabilseydi, izi bırakılacak kişi izi bırakan kuralı
            kendisi kapatabilirdi.
          </p>
        )}
        <ul className="flex flex-col gap-2">
          {kurallar.data.map((k) => (
            <li key={k.id}>
              <KuralKarti kural={k} />
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-2">
        {/*
          ================================================================
          "(0)" DEMEK "SAYAMADIM" DEMEK DEĞİL
          ================================================================
          Başlık `gorunumler.data?.length ?? 0` yazıyordu: istek
          düştüğünde ekran **"Kayıtlı görünümler (0)"** diyor ve altına
          boş bir liste çiziyordu. Yani ölçülemeyen bir şeyi sıfır diye
          bildiriyordu — hem de kendinden emin bir sayıyla.

          İki uç da AYNI izni istiyor (`denetim.log.sistem`), yani
          kurallar geldiyse görünümlerin düşmesi yetki değil **sunucu
          ya da ağ** demek. O hâlde söylenecek şey "yok" değil
          "alınamadı".
        */}
        <h3 className="text-notr-800 text-sm font-semibold">
          Kayıtlı görünümler{gorunumler.data ? ` (${gorunumler.data.length})` : ""}
        </h3>
        {/*
          Paylasilan bir gorunum, kullanicinin goremeyecegi bir kaydi
          GOSTERMEZ: gorunum bir filtre tanimi, bir yetki degil.
        */}
        <p className="text-notr-600 text-xs">
          Görünüm bir filtre tanımıdır, yetki değil — uygulandığında sonuçlar yine sizin
          izinlerinize göre süzülür.
        </p>

        {gorunumler.isError && (
          <p className="text-notr-600 text-sm">
            Kayıtlı görünümler alınamadı. Bu, görünüm olmadığı anlamına gelmez.
          </p>
        )}

        <ul className="flex flex-col gap-1">
          {(gorunumler.data ?? []).map((g) => (
            <Kart key={g.id} olarak="li" className="p-3 text-sm">
              <span className="text-notr-800">{g.ad}</span>
              {g.sistemMi && <Rozet className="ml-2">kurulumla gelen</Rozet>}
              {/*
                PAYLAŞILAN mı KİŞİSEL mi — sunucu söylüyordu, ekran
                atıyordu. Çizim ikisini ayrı işaretliyor ve haklı:
                paylaşılan bir görünümü herkes görüyor, kişisel olanı
                yalnızca sahibi. Aynı listede aynı görünen iki şey,
                birini ötekiyle karıştırmayı davet ediyor.
              */}
              {g.paylasilan && (
                <Rozet ton="bilgi" className="ml-2">
                  paylaşılan
                </Rozet>
              )}
            </Kart>
          ))}
        </ul>
      </section>
    </div>
  );
}

function KuralKarti({ kural: k }: { kural: UyariKurali }) {
  const [acik, setAcik] = useState(false);

  return (
    <div
      className={cn(
        "rounded-kart bg-notr-0 shadow-kart border p-4",
        k.degerlendiriliyor ? "border-notr-200" : "border-uyari-500/40",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-notr-800 text-sm font-medium">{k.ad}</p>
          <p className="text-notr-600 text-xs">
            {k.pencereDk} dakikada {k.esik} olay · soğuma {k.sogumaDk} dk
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {/*
            KILITLI rozeti: bu kural `aktif=false` yapilsa bile
            calisiyor (F-LOG-33). Yazmasaydik, kapatmaya calisip
            "kapanmadi" diye hata sanilirdi.
          */}
          {k.kilitli && <Rozet ton="bilgi">kilitli — kapatılamaz</Rozet>}
          <span
            className={cn(
              "rounded-alan px-2.5 py-0.5 text-xs font-medium",
              !k.degerlendiriliyor
                ? "bg-uyari-500/15 text-uyari-800"
                : k.aktif || k.kilitli
                  ? "bg-basarili-500/15 text-onay-700"
                  : "bg-notr-100 text-notr-600",
            )}
          >
            {!k.degerlendiriliyor
              ? "değerlendirilmiyor"
              : k.aktif || k.kilitli
                ? "çalışıyor"
                : "pasif"}
          </span>
        </div>
      </div>

      <p className="text-notr-600 mt-2 text-xs">
        {/*
          "Hic tetiklenmedi" AYRI bir bilgi: sifir tetiklenme kotu
          degil, ama uzun suredir hic tetiklenmemis bir kural ya
          gercekten sessiz bir sistemi ya da bozuk bir kurali
          gosteriyor.
        */}
        Son tetiklenme: {tarih(k.sonTetiklenmeAt)}
        {k.tetiklenmeSayisi > 0 && ` · toplam ${k.tetiklenmeSayisi} kez`}
      </p>

      <div className="mt-2">
        <Buton gorunum="sessiz" onClick={() => setAcik((a) => !a)}>
          {acik ? "Kapat" : "Eşiği ve pencereyi düzenle"}
        </Buton>
      </div>

      {acik && <KuralDuzenle kural={k} onBitti={() => setAcik(false)} />}
    </div>
  );
}

/**
 * KURAL DÜZENLEME — F-LOG-33
 *
 * ================================================================
 * KİLİTLİ KURALDA NE YAPILABİLECEĞİ ÖNCEDEN YAZILI
 * ================================================================
 * Sunucu gevşetmeyi 409 ile reddediyor. Ekran o kuralı TEKRAR
 * YAZMIYOR — yazsaydı iki yerde iki kural olurdu ve ayrıştıkları gün
 * ekran izin verip sunucu reddederdi.
 *
 * Ekranın yaptığı tek şey KURALI ANLATMAK: hangi yönde
 * değiştirebileceğini önceden söylemek, kullanıcıyı reddedilecek bir
 * denemeye göndermekten iyidir. Karar hâlâ sunucunun.
 */
function KuralDuzenle({ kural: k, onBitti }: { kural: UyariKurali; onBitti: () => void }) {
  const qc = useQueryClient();
  const [esik, setEsik] = useState(String(k.esik));
  const [pencere, setPencere] = useState(String(k.pencereDk));
  const [soguma, setSoguma] = useState(String(k.sogumaDk));

  const mut = useMutation({
    mutationFn: (d: { esik?: number; pencereDk?: number; sogumaDk?: number; aktif?: boolean }) =>
      uyariKuraliGuncelle(k.id, d),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["uyari-kurallari"] });
      onBitti();
    },
  });

  const sayi = (s: string): number | undefined => {
    const n = Number.parseInt(s, 10);
    return Number.isFinite(n) ? n : undefined;
  };

  return (
    <div className="border-notr-200 mt-3 flex flex-col gap-3 border-t pt-3">
      {k.kilitli && (
        <p className="text-notr-600 text-xs">
          Bu kural kilitli: yalnızca <strong>daha duyarlı</strong> yapılabilir — eşik düşürülebilir,
          pencere genişletilebilir, soğuma kısaltılabilir. Kapatılamaz.{" "}
          {/*
            TEK YÖNLÜ OLDUĞU ÖNCEDEN SÖYLENİYOR — ölçülerek görüldü
            (07.09.2026): pencereyi 1'den 5'e genişlettikten sonra
            geri almanın yolu yok, çünkü daraltmak gevşetme sayılıyor.
            Kural doğru; söylenmemesi yanlış olurdu. Sonradan
            "neden geri alamıyorum" diye sormak, önceden bilmekten
            çok daha kötü bir an.
          */}
          <strong>Bu değişiklik geri alınamaz:</strong> gevşetme yönü kapalı olduğu için eski değere
          dönülemez.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <Alan
          etiket="Eşik (olay sayısı)"
          type="number"
          value={esik}
          onChange={(e) => setEsik(e.target.value)}
        />
        <Alan
          etiket="Pencere (dakika)"
          type="number"
          value={pencere}
          onChange={(e) => setPencere(e.target.value)}
        />
        <Alan
          etiket="Soğuma (dakika)"
          type="number"
          value={soguma}
          onChange={(e) => setSoguma(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Buton
          yukleniyor={mut.isPending}
          onClick={() =>
            mut.mutate({ esik: sayi(esik), pencereDk: sayi(pencere), sogumaDk: sayi(soguma) })
          }
        >
          Kaydet
        </Buton>

        {/*
          KİLİTLİ KURALDA KAPATMA DÜĞMESİ HİÇ ÇİZİLMİYOR. Çizip
          sunucuya reddettirmek de "doğru" olurdu ama kullanıcıya
          yapabileceği bir şeymiş gibi gösterirdi. Sunucu yine de
          reddediyor — buradaki gizleme bir kolaylık, koruma değil.
        */}
        {!k.kilitli && (
          <Buton
            gorunum="ikincil"
            yukleniyor={mut.isPending}
            onClick={() => mut.mutate({ aktif: !k.aktif })}
          >
            {k.aktif ? "Kuralı kapat" : "Kuralı aç"}
          </Buton>
        )}
      </div>

      {mut.isError && (
        <p role="alert" className="text-hata-700 text-xs font-medium">
          {mut.error instanceof ApiHatasi ? mut.error.message : "Kural güncellenemedi."}
        </p>
      )}
    </div>
  );
}
